import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { supabase } from '@/lib/supabase';
import { isNativeApp } from '@/lib/platform';

interface VisioHandoffResponse {
  visio_url: string;
  room_id: string;
}

/**
 * Rejoint la salle vidéo d'une réservation confirmée.
 *
 * Natif (Capacitor iOS/Android) : navigation SPA interne vers
 * `/rendez-vous/$reservationId/room`, qui monte `@jeraisonne/visio`'s
 * `RoomPage` directement (même origine que le reste de l'app, session déjà
 * live) — plus de handoff SSO ni de `Browser.open()` externe, donc plus de
 * chrome de navigateur visible : la salle reste "dans" l'app.
 *
 * Web (non natif) : INCHANGÉ — handoff SSO transparent vers `apps/tutor`
 * (tutor.jeraisonne.fr) : l'edge fn `visio-handoff-create` crée/récupère la
 * salle et frappe un jeton à usage unique, embarqué en FRAGMENT d'URL (jamais
 * en query string) pour ne jamais apparaître dans un log d'accès HTTP.
 */
export function useJoinVisioRoom() {
  const navigate = useNavigate();
  return useMutation({
    mutationFn: async (reservationId: string): Promise<void> => {
      if (isNativeApp()) {
        await navigate({ to: '/rendez-vous/$reservationId/room', params: { reservationId } });
        return;
      }

      const { data, error } = await supabase.functions.invoke<VisioHandoffResponse>(
        'visio-handoff-create',
        { body: { reservation_id: reservationId } },
      );
      if (error) throw new Error(error.message);
      if (!data?.visio_url) throw new Error('visio_url_missing');
      window.location.href = data.visio_url;
    },
  });
}
